import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import GridLayout from "react-grid-layout";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaPlus,
  FaSave,
  FaEdit,
  FaTimes,
  FaImages,
  FaTrash,
  FaDownload,
  FaArrowUp,
  FaArrowDown,
  FaBars,
  FaExpandArrowsAlt,
} from "react-icons/fa";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import Notiflix from "notiflix";
import domtoimage from "dom-to-image-more";
import {
  getUUID,
  getVisionBoard,
  postVisionBoard,
  getCurrentPackageUserModule,
} from "../../../services/api";
import { getToken } from "../../../services/cookies";
import Footer from "../../footer";
import ModuleHeader from "../../moduleHeader";

export default function VisionBoard({ id, moduleId, isOwner }) {
  
  const { id: routeUUID } = useParams();
  const navigate = useNavigate();
  const gridRef = useRef(null); // Grid container referansı
  const [boardImages, setBoardImages] = useState([]); // Panodaki görseller
  const [showGallery, setShowGallery] = useState(false); // Galeri modal'ı
  const [editMode, setEditMode] = useState(false);
  const [gridWidth, setGridWidth] = useState(320);
  const [cols, setCols] = useState(3);
  const [selectedImages, setSelectedImages] = useState([]); // Seçili (karartılmış) görseller
  const [layout, setLayout] = useState([]); // Grid layout state: [{ i, x, y, w, h }]
  const [userUUID, setUserUUID] = useState(null); // Kullanıcı UUID'si
  const [loading, setLoading] = useState(false); // Loading durumu
  const [stockImages, setStockImages] = useState([]); // API'den gelen stok görseller
  const [hasPermission, setHasPermission] = useState(false);
  const [imageZIndexes, setImageZIndexes] = useState({}); // Her görsel için z-index
  const [showMenu, setShowMenu] = useState(false);
  const [packageData, setPackageData] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

  const normalizeImageURL = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    // Yerel görseller için olduğu gibi döndür
    if (url.startsWith('/assets/')) return url;
    // API'den gelen görseller için
    if (url.startsWith('/')) return `https://be.jodi.ac${url}`;
    return `https://be.jodi.ac/${url}`;
  };

  const createSafeImageURL = (originalURL) => {
    const normalizedURL = normalizeImageURL(originalURL);
    // Sadece API görselleri için cache busting
    if (normalizedURL.includes('be.jodi.ac')) {
      return normalizedURL + '?t=' + Date.now();
    }
    return normalizedURL;
  };

  const loadImageWithCORS = async (src) => {
    // Yerel görseller için basit yükleme
    if (src.startsWith('/assets/')) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Görsel yüklenemedi'));
        img.src = src;
      });
    }

    // API görselleri için CORS ile yükleme
    try {
      const response = await fetch(src, { mode: 'cors', credentials: 'omit' });
      if (response.ok) {
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            URL.revokeObjectURL(imageUrl);
            resolve(img);
          };
          img.onerror = () => {
            URL.revokeObjectURL(imageUrl);
            reject(new Error('Görsel yüklenemedi'));
          };
          img.src = imageUrl;
        });
      }
    } catch (error) {
      console.error('Görsel yükleme hatası:', error);
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Görsel yüklenemedi'));
      img.src = src;
    });
  };

  // Paket ve kullanıcı bilgilerini çek (moduleId sabit 5 - Vision Board)
  useEffect(() => {
    if (!id) return;
    
    const fetchPackageData = async () => {
      try {
        const data = await getCurrentPackageUserModule(id, 5);
        setPackageData(data);
      } catch (error) {
        console.error("Paket verisi alınamadı:", error);
      } finally {
        setPageLoading(false);
      }
    };

    fetchPackageData();
  }, [id]);

  useEffect(() => {
    Notiflix.Notify.init({
      position: "top-right",
      timeout: 3000,
      clickToClose: true,
      pauseOnHover: true,
    });

    // Yerel görselleri yükle
    const localStockImages = [
      { id: 1, src: '/assets/visionBoard/vb-work.jpg', originalSrc: '/assets/visionBoard/vb-work.jpg', title: 'İş - Kariyer', type: 'stock', category: 'work', original_id: 1 },
      { id: 2, src: '/assets/visionBoard/vb-work-2.jpg', originalSrc: '/assets/visionBoard/vb-work-2.jpg', title: 'İş - Kariyer 2', type: 'stock', category: 'work', original_id: 2 },
      { id: 3, src: '/assets/visionBoard/vb-dicipline.jpg', originalSrc: '/assets/visionBoard/vb-dicipline.jpg', title: 'Disiplin', type: 'stock', category: 'discipline', original_id: 3 },
      { id: 4, src: '/assets/visionBoard/vb-friends.jpg', originalSrc: '/assets/visionBoard/vb-friends.jpg', title: 'Arkadaşlık', type: 'stock', category: 'friends', original_id: 4 },
      { id: 5, src: '/assets/visionBoard/vb-friends-2.jpg', originalSrc: '/assets/visionBoard/vb-friends-2.jpg', title: 'Arkadaşlık 2', type: 'stock', category: 'friends', original_id: 5 },
      { id: 6, src: '/assets/visionBoard/vb-road.jpg', originalSrc: '/assets/visionBoard/vb-road.jpg', title: 'Yol - Hedef', type: 'stock', category: 'goal', original_id: 6 },
      { id: 7, src: '/assets/visionBoard/vb-success.jpg', originalSrc: '/assets/visionBoard/vb-success.jpg', title: 'Başarı', type: 'stock', category: 'success', original_id: 7 },
      { id: 8, src: '/assets/visionBoard/vb-success-2.jpg', originalSrc: '/assets/visionBoard/vb-success-2.jpg', title: 'Başarı 2', type: 'stock', category: 'success', original_id: 8 },
      { id: 9, src: '/assets/visionBoard/vb-love.jpg', originalSrc: '/assets/visionBoard/vb-love.jpg', title: 'Aşk', type: 'stock', category: 'love', original_id: 9 },
      { id: 10, src: '/assets/visionBoard/vb-love-2.jpg', originalSrc: '/assets/visionBoard/vb-love-2.jpg', title: 'Aşk 2', type: 'stock', category: 'love', original_id: 10 },
      { id: 11, src: '/assets/visionBoard/vb-marriage.jpg', originalSrc: '/assets/visionBoard/vb-marriage.jpg', title: 'Evlilik', type: 'stock', category: 'marriage', original_id: 11 },
      { id: 12, src: '/assets/visionBoard/vb-marriage-2.jpg', originalSrc: '/assets/visionBoard/vb-marriage-2.jpg', title: 'Evlilik 2', type: 'stock', category: 'marriage', original_id: 12 },
      { id: 13, src: '/assets/visionBoard/vb-family.jpg', originalSrc: '/assets/visionBoard/vb-family.jpg', title: 'Aile', type: 'stock', category: 'family', original_id: 13 },
      { id: 14, src: '/assets/visionBoard/vb-family-2.jpg', originalSrc: '/assets/visionBoard/vb-family-2.jpg', title: 'Aile 2', type: 'stock', category: 'family', original_id: 14 },
      { id: 15, src: '/assets/visionBoard/vb-car.jpg', originalSrc: '/assets/visionBoard/vb-car.jpg', title: 'Araba', type: 'stock', category: 'car', original_id: 15 },
      { id: 16, src: '/assets/visionBoard/vb-health.jpg', originalSrc: '/assets/visionBoard/vb-health.jpg', title: 'Sağlık', type: 'stock', category: 'health', original_id: 16 },
      { id: 17, src: '/assets/visionBoard/vb-health-2.jpg', originalSrc: '/assets/visionBoard/vb-health-2.jpg', title: 'Sağlık 2', type: 'stock', category: 'health', original_id: 17 },
      { id: 18, src: '/assets/visionBoard/vb-money.jpg', originalSrc: '/assets/visionBoard/vb-money.jpg', title: 'Para - Zenginlik', type: 'stock', category: 'money', original_id: 18 },
      { id: 19, src: '/assets/visionBoard/vb-money-2.jpg', originalSrc: '/assets/visionBoard/vb-money-2.jpg', title: 'Para - Zenginlik 2', type: 'stock', category: 'money', original_id: 19 },
      { id: 20, src: '/assets/visionBoard/vb-travelworld.jpg', originalSrc: '/assets/visionBoard/vb-travelworld.jpg', title: 'Dünya Gezisi', type: 'stock', category: 'travel', original_id: 20 },
      { id: 21, src: '/assets/visionBoard/vb-travelworld-2.jpg', originalSrc: '/assets/visionBoard/vb-travelworld-2.jpg', title: 'Dünya Gezisi 2', type: 'stock', category: 'travel', original_id: 21 },
    ];
    
    setStockImages(localStockImages);
  }, []);

  useEffect(() => {
    const fetchBoardByParam = async () => {
      if (!routeUUID) return;
      try {
        setLoading(true);
        const boardResponse = await getVisionBoard(routeUUID);
        if (boardResponse?.data?.images) {
          const images = boardResponse.data.images;
          const processedImages = images.map((img) => ({
            id: String(img.id),
            src: normalizeImageURL(img.src),
            originalSrc: img.src,
            title: img.title,
            type: img.type,
            originalId: img.original_id,
            isSelected: img.is_selected,
            position: img.position,
            zIndex: img.grid?.z_index || 1,
          }));
          const processedLayout = images
            .filter((img) => img.grid && img.grid !== null)
            .map((img) => ({
              i: String(img.id),
              x: img.grid.x,
              y: img.grid.y,
              w: img.grid.w,
              h: img.grid.h,
              moved: img.grid.moved || false,
              static: img.grid.static || false,
            }))
            .sort((a, b) => {
              if (a.y !== b.y) return a.y - b.y;
              return a.x - b.x;
            });
          const selectedImagesLocal = images
            .filter((img) => img.is_selected)
            .map((img) => String(img.id));

          const zIndexData = {};
          processedImages.forEach(img => {
            zIndexData[img.id] = img.zIndex || 1;
          });

          setBoardImages(processedImages);
          setLayout(processedLayout);
          setSelectedImages(selectedImagesLocal);
          setImageZIndexes(zIndexData);
        }
      } catch (error) {
        console.error("Vision Board yüklenirken hata:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBoardByParam();
  }, [routeUUID]);

  const checkPermission = async () => {
    try {
      const token = getToken('jodiactoken');
      if (!token) {
        setHasPermission(false);
        return;
      }
      const uuidResp = await getUUID();
      setHasPermission(uuidResp?.uuid == routeUUID);
      setUserUUID(uuidResp?.uuid);
    } catch (e) {
      setHasPermission(false);
    }
  };

  useEffect(() => {
    checkPermission();
  }, [routeUUID]);

  const fetchProtectedDataOnOpen = async () => {
    try {
      setLoading(true);
      
      // UUID'yi sadece henüz yoksa çek
      if (!userUUID) {
        const uuidResponse = await getUUID();
        setUserUUID(uuidResponse.uuid);
      }
    } catch (error) {
      console.error("Veri alınırken hata:", error);
      if (error?.response?.status === 401) {
        Notiflix.Notify.failure("Devam etmek için giriş yapın.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const updateGridWidth = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 640) {
        setGridWidth(screenWidth);
        setCols(8);
      } else {
        setGridWidth(screenWidth );
        setCols(16);
      }
    };
    updateGridWidth();
    window.addEventListener("resize", updateGridWidth);
    return () => window.removeEventListener("resize", updateGridWidth);
  }, []);

  const selectPhoto = (photo) => {
    const stringId = String(photo.id);
    const isAlreadyOnBoard = boardImages.some(img => img.id === stringId);
    
    if (isAlreadyOnBoard) {
      Notiflix.Notify.warning("Bu görsel zaten panonuzda mevcut!");
      return;
    }

    const newBoardImage = {
      id: stringId,
      src: normalizeImageURL(photo.src),
      originalSrc: photo.src,
      title: photo.title,
      originalId: photo.original_id || photo.id,
      type: photo.type,
      isSelected: false,
      position: boardImages.length,
    };
    setBoardImages((prev) => [...prev, newBoardImage]);

    // Mevcut en yüksek z-index'i bul ve 1 ekle
    const maxZIndex = Object.values(imageZIndexes).length > 0 
      ? Math.max(...Object.values(imageZIndexes)) 
      : 0;
    
    setImageZIndexes(prev => ({
      ...prev,
      [stringId]: maxZIndex + 1
    }));

    setLayout((prev) => {
      const nextItem = {
        i: stringId,
        x: Math.floor(Math.random() * (cols - 6)),
        y: Math.floor(Math.random() * 10),
        w: 6,
        h: 6,
        moved: false,
        static: false,
        minW: 1,
        maxW: cols,
        minH: 1,
        maxH: 24,
      };
      return [...prev, nextItem];
    });
    setShowGallery(false);
    
    // Düzenleme modunu aç
    setEditMode(true);
  };


  const removeFromBoard = (imageId) => {
    setBoardImages((prev) => prev.filter((img) => img.id !== imageId));
    setSelectedImages((prev) => prev.filter((id) => id !== imageId));
    setLayout((prev) => prev.filter((item) => item.i !== imageId));
    setImageZIndexes(prev => {
      const newZIndexes = { ...prev };
      delete newZIndexes[imageId];
      return newZIndexes;
    });
  };


  const toggleImageSelection = (imageId) => {
    if (editMode) return;
    setSelectedImages((prev) => {
      if (prev.includes(imageId)) {
        return prev.filter((id) => id !== imageId);
      } else {
        return [...prev, imageId];
      }
    });
  };

  const increaseZIndex = (imageId) => {
    setImageZIndexes(prev => ({
      ...prev,
      [imageId]: (prev[imageId] || 1) + 1
    }));
  };

  const decreaseZIndex = (imageId) => {
    setImageZIndexes(prev => ({
      ...prev,
      [imageId]: Math.max((prev[imageId] || 1) - 1, 0)
    }));
  };

  const downloadGrid = async () => {
    if (!gridRef.current) {
      Notiflix.Notify.failure("Grid bulunamadı!");
      return;
    }

    if (boardImages.length === 0) {
      Notiflix.Notify.warning("İndirilecek görsel bulunamadı!");
      return;
    }

    try {
      setLoading(true);
      Notiflix.Notify.info("Vision Board indiriliyor...");
      
      const blob = await downloadWithDomToImage();
      if (blob) {
        await saveBlob(blob, `vision-board-${new Date().getTime()}.png`);
        Notiflix.Notify.success("Vision Board başarıyla indirildi!");
        return;
      }

      const canvas = await createManualCanvas();
      if (canvas) {
        const link = document.createElement("a");
        link.download = `vision-board-${new Date().getTime()}.png`;
        link.href = canvas.toDataURL();
        link.click();
        Notiflix.Notify.success("Vision Board başarıyla indirildi!");
        return;
      }

      throw new Error("İndirme başarısız");

    } catch (error) {
      console.error("Vision Board indirme hatası:", error);
      Notiflix.Notify.failure("Vision Board indirilemedi!");
    } finally {
      setLoading(false);
    }
  };

  const downloadWithDomToImage = async () => {
    try {
      const editControls = gridRef.current.querySelectorAll('.no-drag');
      editControls.forEach(control => {
        control.style.display = 'none';
      });
      
      // GridLayout'u direkt yakala (boşluk olmadan)
      const gridLayout = gridRef.current.querySelector('.react-grid-layout') || gridRef.current;
      
      // Tüm görsellerin shadow'larını geçici olarak kaldır
      const allImages = gridRef.current.querySelectorAll('.react-grid-item > div');
      const originalShadows = [];
      allImages.forEach(img => {
        originalShadows.push(img.style.boxShadow);
        img.style.boxShadow = 'none';
      });
      
      // Container'ın shadow'unu kaldır
      const originalContainerShadow = gridRef.current.style.boxShadow;
      gridRef.current.style.boxShadow = 'none';
      
      const options = {
        quality: 1,
        bgcolor: '#ffffff',
        width: window.innerWidth || 1920,
        height: Math.max(1000, gridLayout.scrollHeight),
        filter: (node) => {
          if (node.classList && (
            node.classList.contains('no-drag') ||
            node.classList.contains('react-resizable-handle')
          )) {
            return false;
          }
          if (node.style) {
            const computedStyle = window.getComputedStyle(node);
            for (let property of computedStyle) {
              const value = computedStyle.getPropertyValue(property);
              if (value && value.includes('oklch')) {
                node.style.setProperty(property, '#ffffff', 'important');
              }
            }
          }
          return true;
        }
      };

      const blob = await domtoimage.toBlob(gridLayout, options);
      
      // Shadow'ları geri yükle
      allImages.forEach((img, index) => {
        img.style.boxShadow = originalShadows[index];
      });
      gridRef.current.style.boxShadow = originalContainerShadow;
      
      editControls.forEach(control => {
        control.style.display = '';
      });
      return blob;
    } catch (error) {
      console.error("DOM-to-Image hatası:", error);
      const editControls = gridRef.current.querySelectorAll('.no-drag');
      editControls.forEach(control => {
        control.style.display = '';
      });
      return null;
    }
  };

  const createManualCanvas = async () => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Canvas'ı tam ekran genişliğinde ve yeterli yükseklikte oluştur (boşluksuz)
      const canvasWidth = window.innerWidth || 1920;
      const rowHeight = 30; // GridLayout ile aynı
      const canvasHeight = Math.max(1000, 
        layout.reduce((maxY, item) => Math.max(maxY, (item.y + item.h) * rowHeight), 0)
      );
      
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const sortedImages = [...boardImages].sort((a, b) => 
        (imageZIndexes[a.id] || 1) - (imageZIndexes[b.id] || 1)
      );
      
      let loadedCount = 0;
      const totalImages = sortedImages.length;
      
      return new Promise((resolve) => {
        if (totalImages === 0) {
          resolve(canvas);
          return;
        }

        sortedImages.forEach(async (img) => {
          const layoutItem = layout.find(l => l.i === img.id);
          if (!layoutItem) {
            loadedCount++;
            if (loadedCount === totalImages) resolve(canvas);
            return;
          }

          try {
            const imageSrc = createSafeImageURL(img.originalSrc || img.src);
            const imageElement = await loadImageWithCORS(imageSrc);
            
            // Grid koordinatlarını canvas koordinatlarına çevir (boşluksuz)
            const x = (layoutItem.x * canvasWidth) / cols;
            const y = layoutItem.y * rowHeight;
            const w = (layoutItem.w * canvasWidth) / cols;
            const h = layoutItem.h * rowHeight;
            
            // Görseli çiz (çerçeve olmadan)
            ctx.drawImage(imageElement, x, y, w, h);
          } catch (error) {
            console.error('Görsel yüklenemedi:', error);
          }
          
          loadedCount++;
          if (loadedCount === totalImages) resolve(canvas);
        });
        
        setTimeout(() => resolve(canvas), 15000);
      });
      
    } catch (error) {
      console.error("Manuel canvas hatası:", error);
      return null;
    }
  };

  const saveBlob = async (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleSave = async () => {
    if (!userUUID) {
      Notiflix.Notify.failure("Kullanıcı bilgisi bulunamadı!");
      return;
    }

    try {
      setLoading(true);

      const saveData = {
        uuid: userUUID,
        images: boardImages.map((img, index) => {
          const gridItem = layout.find((l) => l.i === img.id);
          const safeGrid = gridItem || {
            w: 1,
            h: 1,
            x: 0,
            y: 0,
            moved: false,
            static: false,
          };
          
          return {
            id: img.id,
            src: img.src,
            title: img.title,
            type: img.type,
            original_id: String(img.originalId || img.id),
            position: index,
            is_selected: selectedImages.includes(img.id),
            grid: {
              w: safeGrid.w,
              h: safeGrid.h,
              x: safeGrid.x,
              y: safeGrid.y,
              i: img.id,
              moved: safeGrid.moved,
              static: safeGrid.static,
              z_index: imageZIndexes[img.id] || 1,
            },
          };
        }),
      };

      await postVisionBoard(saveData);
      Notiflix.Notify.success("Vision Board başarıyla kaydedildi!");
      
      // Düzenleme modundan çık
      setEditMode(false);
    } catch (error) {
      console.error("Vision Board kaydedilirken hata:", error);
      Notiflix.Notify.failure("Vision Board kaydedilirken bir hata oluştu!");
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading || !packageData) {
    return (
      <div className="w-full h-screen min-h-screen flex items-center justify-center bg-[#fff]">
        <div className="flex flex-col items-center justify-center">
          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-24 w-24 border-4 border-transparent border-t-[#3DA7B5] border-r-[#3DA7B5] opacity-75"></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="animate-spin rounded-full h-16 w-16 border-[3px] border-transparent border-b-[#2a8a96] border-l-[#2a8a96] opacity-60"
                style={{
                  animationDirection: "reverse",
                  animationDuration: "1.5s",
                }}
              ></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#3DA7B5] via-[#2a8a96] to-[#3DA7B5] animate-pulse shadow-2xl"></div>
            </div>
          </div>
          <p className="text-xl font-semibold text-gray-800 animate-pulse">
            Yükleniyor...
          </p>
        </div>
      </div>
    );
  }

  if (loading && boardImages.length === 0) {
    return (
      <div className="p-2 pb-24 min-h-screen sm:p-4 bg-white w-full max-w-full mx-auto flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Vision Board yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed] relative">
        <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">
          {/* ModuleHeader */}
          {packageData && (
            <div className="relative z-30 w-full mb-2">
              <ModuleHeader
                userName={
                  packageData.user?.first_name +
                  " " +
                  packageData.user?.last_name
                }
                subtitle={packageData.package?.name + " Modülüne Hoş Geldiniz"}
                title={packageData.package?.name}
                description={packageData.package?.description}
                backgroundImage={packageData.package?.image_url}
                onExpandedChange={setIsHeaderExpanded}
                showToggle={true}
                autoHeight={true}
              />
            </div>
          )}

          <div className="pb-24 min-h-screen bg-white w-full">
            {/* Menü Butonu - Vision Board Üstü */}
            <div className="px-3 pb-2">
              <button
                onClick={() => setShowMenu(true)}
                className="w-full flex items-center justify-center gap-2 bg-[#8E97FD] text-white py-2.5 px-4 hover:bg-[#818ae7] transition-colors mb-0"
              >
                <FaBars className="w-4 h-4" />
                <span className="text-sm font-medium">Hayal Panosu Menüsü</span>
              </button>
            </div>

            <div 
              ref={gridRef}
              className="bg-white mb-4 min-h-[600px] overflow-hidden"
            >
              {boardImages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 sm:h-64 text-gray-500">
                  <FaImages className="w-12 h-12 sm:w-16 sm:h-16 mb-4 opacity-50" />
                  <p className="text-base sm:text-lg mb-2 text-center">
                    Vision Board'unuz boş
                  </p>
                  <p className="text-xs sm:text-sm text-center px-4">
                    Görsel eklemek için yukarıdaki butona tıklayın
                  </p>
                </div>
              ) : (
                <div className="w-full overflow-hidden">
                  <style>{`
                    .react-grid-item {
                      position: absolute !important;
                    }
                    .react-grid-layout {
                      position: relative !important;
                    }
                  `}</style>
                  <GridLayout
                    className="layout"
                    style={{ position: 'relative' }}
                    cols={cols}
                    rowHeight={30}
                    width={gridWidth}
                    isDraggable={editMode}
                    isResizable={editMode}
                    draggableCancel=".no-drag"
                    preventCollision={false}
                    compactType={null}
                    allowOverlap={true}
                    margin={[0, 0]}
                    containerPadding={[0, 0]}
                    maxRows={200}
                    useCSSTransforms={true}
                    layout={layout}
                    onLayoutChange={(newLayout) => {
                      const validLayout = newLayout.map(item => ({
                        ...item,
                        x: item.x,
                        y: item.y,
                        w: Math.max(1, item.w),
                        h: Math.max(1, item.h),
                      }));
                      setLayout(validLayout);
                    }}
                    resizeHandles={["se"]}
                    resizeHandle={(axis, ref) => (
                      <div
                        className={`react-resizable-handle react-resizable-handle-${axis}`}
                        ref={ref}
                        style={{
                          right: '8px',
                          bottom: '8px',
                        }}
                      >
                        <div className="w-6 h-6 bg-white rounded-lg opacity-80 hover:opacity-100 flex items-center justify-center transition-opacity shadow-md">
                          <FaExpandArrowsAlt className="w-3 h-3 text-black" />
                        </div>
                      </div>
                    )}
                  >
              {boardImages.map((img, index) => {
                const gridLayout = layout.find((l) => l.i === img.id);
                const gridData = gridLayout || {
                  x: index % cols,
                  y: Math.floor(index / cols),
                  w: 2,
                  h: 2,
                  minW: 2,
                  maxW: cols,
                  minH: 2,
                  maxH: 8,
                };

                return (
                  <div 
                    key={img.id} 
                    data-grid={gridData}
                    style={{ 
                      zIndex: imageZIndexes[img.id] || 1,
                      position: 'absolute'
                    }}
                  >
                    <motion.div
                      whileTap={{ scale: 0.97 }}
                      className="relative w-full h-full overflow-hidden group bg-gray-100 cursor-pointer"
                      onClick={() => toggleImageSelection(img.id)}
                    >
                      <img
                        src={img.src}
                        alt="Vision board image"
                        className="object-cover w-full h-full"
                        onError={(e) => {
                          if (img.originalSrc && e.target.src !== img.originalSrc) {
                            e.target.src = img.originalSrc;
                          } else {
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect width="200" height="200" fill="%23f0f0f0"/%3E%3Ctext x="50%" y="50%" font-family="Arial" font-size="14" fill="%23999" text-anchor="middle" dy=".3em"%3EGörsel Yok%3C/text%3E%3C/svg%3E';
                          }
                        }}
                      />
                      
                      {editMode && (
                        <div className="no-drag absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1 py-0.5 rounded">
                          Z:{imageZIndexes[img.id] || 1}
                        </div>
                      )}

                      {selectedImages.includes(img.id) && !editMode && (
                        <div className="absolute inset-0 bg-black/40"></div>
                      )}
                      
                      {editMode && (
                        <>
                          <div className="no-drag absolute top-1 left-1 flex flex-col gap-1">
                            <button
                              onMouseDown={(e) => e.stopPropagation()}
                              onTouchStart={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                increaseZIndex(img.id);
                              }}
                              className="bg-white text-black rounded w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center opacity-70 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity hover:bg-gray-100 shadow-md"
                              title="Z-index artır"
                            >
                              <FaArrowUp className="w-2 h-2 sm:w-3 sm:h-3" />
                            </button>
                            <button
                              onMouseDown={(e) => e.stopPropagation()}
                              onTouchStart={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                decreaseZIndex(img.id);
                              }}
                              className="bg-white text-black rounded w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center opacity-70 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity hover:bg-gray-100 shadow-md"
                              title="Z-index azalt"
                            >
                              <FaArrowDown className="w-2 h-2 sm:w-3 sm:h-3" />
                            </button>
                          </div>

                          <button
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFromBoard(img.id);
                            }}
                            className="no-drag absolute top-1 right-1 bg-white text-black rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center opacity-70 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity z-10 hover:bg-gray-100 shadow-md"
                          >
                            <FaTimes className="w-2 h-2 sm:w-3 sm:h-3" />
                          </button>
                        </>
                      )}
                    </motion.div>
                  </div>
                );
              })}
                  </GridLayout>
                </div>
              )}
            </div>

            <AnimatePresence>
              {showGallery && (
                <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            className="fixed inset-0 bg-white z-50 flex flex-col overflow-hidden"
          >
            <div className="flex justify-between items-center p-4 border-b bg-white flex-shrink-0">
              <h3 className="text-lg sm:text-xl font-semibold">Görsel Seç</h3>
              <button
                onClick={() => setShowGallery(false)}
                className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* Başlık */}
            <div className="flex items-center gap-2 px-4 py-3 border-b bg-white flex-shrink-0">
              <FaImages className="w-5 h-5 text-[#8E97FD]" />
              <h3 className="text-base font-semibold text-gray-800">
                Fotoğraflar ({stockImages.length})
              </h3>
            </div>

            <div className="flex-1 overflow-y-auto bg-gray-50">
              <div className="p-4 pb-8">
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4 mb-16">
                {stockImages.map((photo) => (
                    <motion.div
                      key={photo.id}
                      whileTap={{ scale: 0.95 }}
                      className="relative aspect-square cursor-pointer rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-200 bg-white"
                      onClick={() => selectPhoto(photo)}
                    >
                      <img
                        src={photo.src}
                        alt={photo.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          if (photo.originalSrc && e.target.src !== photo.originalSrc) {
                            e.target.src = photo.originalSrc;
                          } else {
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect width="200" height="200" fill="%23f0f0f0"/%3E%3Ctext x="50%" y="50%" font-family="Arial" font-size="14" fill="%23999" text-anchor="middle" dy=".3em"%3EGörsel Bulunamadı%3C/text%3E%3C/svg%3E';
                          }
                        }}
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-200 flex items-end">
                        <div className="w-full p-2 bg-gradient-to-t from-black/70 to-transparent">
                          <p className="text-white text-xs truncate">
                            {photo.title}
                          </p>
                        </div>
                      </div>
                      <div className="absolute top-2 right-2 bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <FaPlus className="w-3 h-3" />
                      </div>
                    </motion.div>
                  )
                )}
              </div>

              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sol Menü Drawer */}
      <AnimatePresence>
        {showMenu && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMenu(false)}
              className="fixed inset-0 bg-black/50 z-50"
            />
            
            {/* Drawer Menü - Modern Tasarım */}
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 h-full w-80 bg-gradient-to-br from-white to-gray-50 shadow-2xl z-50 flex flex-col backdrop-blur-lg"
            >
              {/* Header - Modern */}
              <div className="bg-gradient-to-r from-[#8E97FD] to-[#A5AFFF] p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-1">Hayal Panom</h3>
                    <p className="text-white/80 text-sm">Rüyalarını tasarla</p>
                  </div>
                  <button
                    onClick={() => setShowMenu(false)}
                    className="text-white/80 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-all"
                  >
                    <FaTimes className="w-5 h-5" />
                  </button>
                </div>
                {boardImages.length > 0 && (
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 text-white">
                    <p className="text-xs opacity-80 mb-1">Toplam Görsel</p>
                    <p className="text-2xl font-bold">{boardImages.length}</p>
                  </div>
                )}
              </div>

              {/* Menü İçeriği - Modern */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-2">
                  {/* Görsel Ekle */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={async () => {
                      setShowMenu(false);
                      await fetchProtectedDataOnOpen();
                      setShowGallery(true);
                    }}
                    className="flex items-center gap-4 bg-gradient-to-r from-[#8E97FD] to-[#A5AFFF] text-white px-5 py-4 rounded-2xl hover:shadow-lg transition-all w-full text-left group"
                  >
                    <div className="bg-white/20 p-2 rounded-lg group-hover:bg-white/30 transition-all">
                      <FaPlus className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="font-semibold text-base">Görsel Ekle</span>
                      <p className="text-xs text-white/70">Yeni resim seç</p>
                    </div>
                  </motion.button>
                  
                  {/* Düzenle */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowMenu(false);
                      setEditMode(!editMode);
                    }}
                    disabled={boardImages.length === 0}
                    className="flex items-center gap-4 bg-white text-gray-800 px-5 py-4 rounded-2xl hover:shadow-md transition-all w-full text-left border border-gray-200 group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="bg-gray-100 p-2 rounded-lg group-hover:bg-gray-200 transition-all">
                      <FaEdit className="w-5 h-5 text-gray-700" />
                    </div>
                    <div>
                      <span className="font-semibold text-base">
                        {editMode ? "Düzenlemeyi Kapat" : "Düzenle"}
                      </span>
                      <p className="text-xs text-gray-500">Taşı & boyutlandır</p>
                    </div>
                  </motion.button>

                  {/* Kaydet */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowMenu(false);
                      handleSave();
                    }}
                    disabled={loading}
                    className="flex items-center gap-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-5 py-4 rounded-2xl hover:shadow-lg transition-all w-full text-left group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="bg-white/20 p-2 rounded-lg group-hover:bg-white/30 transition-all">
                      <FaSave className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="font-semibold text-base">
                        {loading ? "Kaydediliyor..." : "Kaydet"}
                      </span>
                      <p className="text-xs text-white/70">Değişiklikleri sakla</p>
                    </div>
                  </motion.button>
                </div>

                {/* İpucu Kartı - Modern */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mt-6 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100"
                >
                  <div className="flex items-start gap-3">
                    <div className="bg-blue-500 text-white p-2 rounded-lg flex-shrink-0">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800 mb-1">İpucu</p>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        Görselleri sürükle-bırak ile düzenleyebilir, boyutlandırabilir ve üst üste koyabilirsiniz.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
                </motion.div>
              </>
              )}
            </AnimatePresence>

            {/* Düzenleme Modunda Sabit Kaydet Butonu - Sağ Alt Köşe */}
            {editMode && (
              <div className="fixed bottom-20 right-4 sm:right-auto sm:left-1/2 sm:transform sm:translate-x-[175px] z-50">
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="w-14 h-14 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:scale-110 active:scale-95"
                  title={loading ? "Kaydediliyor..." : "Kaydet"}
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <FaSave className="w-6 h-6" />
                  )}
                </button>
              </div>
            )}

            <Footer type={0} />
          </div>
        </div>
      </div>
    </>
  );
}

